import React, { useEffect, useState } from "react";
import {
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormControlLabel,
    Checkbox,
    Button,
    Box,
    Typography,
    CircularProgress,
    Snackbar,
    Alert,
} from "@mui/material";
import { motion } from "framer-motion";
import { BASE_URL } from "../../utils/endpoints";


const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5,
            ease: "easeOut",
            staggerChildren: 0.1,
        },
    },
    exit: {
        opacity: 0,
        y: -50,
        transition: { duration: 0.3, ease: "easeIn" },
    },
};


const fieldVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.4, ease: "easeOut" },
    },
};


const buttonVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.4, ease: "easeOut" },
    },
    hover: {
        scale: 1.05,
        transition: { duration: 0.2 },
    },
};

const SliderCreate = ({ onSliderCreated, handleClose }) => {
    
    const [sliderLink, setSliderLink] = useState("");
    const [sliderImg, setSliderImg] = useState("");
    const [clientName, setClientName] = useState("");
    const [trending, setTrending] = useState(false);
    const [status, setStatus] = useState("live");
    const [brands, setBrands] = useState([]);

    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    
    useEffect(() => {
        const fetchBrands = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${BASE_URL}/brands`);
                const data = await response.json();
                if (data && data.brands) {
                    setBrands(data.brands);
                } else {
                    setError("Unexpected response format for brands.");
                }
            } catch (error) {
                console.error("Error fetching brands:", error);
                setError("Failed to fetch brands. Please try again.");
                setSnackbarMessage("Failed to fetch brands. Please try again.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            } finally {
                setLoading(false);
            }
        };

        fetchBrands();
    }, []);

    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const data = {
            slider_link: sliderLink,
            slider_img: sliderImg,
            client_name: clientName,
            Trending: trending,
            status: status,
        };

        ("Submitting data:", data);

        try {
            const response = await fetch(`${BASE_URL}/sliders`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(data),
            });

            const result = await response.json();
            ("Response:", result);

            if (response.ok) {
                onSliderCreated(result);
                setSnackbarMessage("Slider created successfully!");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
                handleClose();
            } else {
                const errorMessage = result.message || "Failed to create slider. Please check your inputs.";
                setError(errorMessage);
                setSnackbarMessage(errorMessage);
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            }
        } catch (error) {
            console.error("Error submitting slider:", error);
            const errorMessage = "Error submitting slider. Please try again.";
            setError(errorMessage);
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    
    const handleSnackbarClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbarOpen(false);
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
        >
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    width: "100%",
                }}
            >
                {sliderImg && (
                    <motion.div variants={fieldVariants} style={{ display: sliderImg ? 'block' : 'none' }}>
                        <Box
                            sx={{
                                border: "1px solid #ccc",
                                borderRadius: "8px",
                                padding: "10px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                overflow: "hidden",
                                minHeight: "100px",
                            }}
                        >
                            <img
                                src={sliderImg}
                                alt="Slider Preview"
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    height: "auto",
                                    borderRadius: "4px",
                                    objectFit: "contain",
                                }}
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://placehold.co/200x150/EEEEEE/333333?text=Image+Load+Error";
                                }}
                            />
                        </Box>
                    </motion.div>
                )}
                {error && (
                    <motion.div variants={fieldVariants}>
                        <Typography color="error" variant="body2">
                            {error}
                        </Typography>
                    </motion.div>
                )}

                <motion.div variants={fieldVariants}>
                    <TextField
                        label="Slider Link"
                        id="sliderLink"
                        value={sliderLink}
                        onChange={(e) => setSliderLink(e.target.value)}
                        required
                        fullWidth
                        variant="outlined"
                        disabled={loading}
                        InputLabelProps={{ style: { color: "#2e7d32" } }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#2e7d32' },
                                '&:hover fieldset': { borderColor: '#2e7d32' },
                                '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                            },
                        }}
                    />
                </motion.div>

                <motion.div variants={fieldVariants}>
                    <TextField
                        label="Slider Image URL"
                        id="sliderImg"
                        value={sliderImg}
                        onChange={(e) => setSliderImg(e.target.value)}
                        required
                        fullWidth
                        variant="outlined"
                        disabled={loading}
                        InputLabelProps={{ style: { color: "#2e7d32" } }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#2e7d32' },
                                '&:hover fieldset': { borderColor: '#2e7d32' },
                                '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                            },
                            color: "#2e7d32",
                        }}
                    />
                </motion.div>

                <motion.div variants={fieldVariants}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 2,
                            alignItems: "center",
                            width: "100%",
                        }}
                    >
                        <FormControl
                            variant="outlined"
                            required
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: '#2e7d32' },
                                    '&:hover fieldset': { borderColor: '#2e7d32' },
                                    '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                },
                                flex: 1,
                            }}
                        >
                            <InputLabel
                                id="clientName-label"
                                sx={{
                                    '&.Mui-focused': {
                                        color: '#2e7d32', 
                                    },
                                    '&.MuiFormLabel-filled': {
                                        color: '#2e7d32', 
                                    },
                                }}
                            >
                                Select Brand
                            </InputLabel>
                            <Select
                                labelId="clientName-label"
                                id="clientName"
                                value={clientName}
                                onChange={(e) => setClientName(e.target.value)}
                                label="Select Brand"
                                disabled={loading}
                            >
                                <MenuItem value="">
                                    <em>-- Select Brand --</em>
                                </MenuItem>
                                {brands.map((brand) => (
                                    <MenuItem key={brand._id} value={brand.brand_name}>
                                        {brand.brand_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    id="trending"
                                    checked={trending}
                                    onChange={(e) => setTrending(e.target.checked)}
                                    disabled={loading}
                                    sx={{
                                        color: "#2e7d32",
                                        '&.Mui-checked': {
                                            color: "#2e7d32",
                                        },
                                    }}
                                />
                            }
                            label="Trending"
                        />
                    </Box>
                </motion.div>

                <motion.div variants={fieldVariants}>
                    <FormControl fullWidth variant="outlined"
                        sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#2e7d32' },
                                '&:hover fieldset': { borderColor: '#2e7d32' },
                                '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                            },
                        }}
                    >
                        <InputLabel id="status-label" sx={{
                            color: '#2e7d32',
                            '&.Mui-focused': {
                                color: '#2e7d32', 
                            },
                            '&.MuiFormLabel-filled': {
                                color: '#2e7d32', 
                            },
                        }}
                        >Status</InputLabel>
                        <Select
                            labelId="status-label"
                            id="status"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            label="Status"
                            disabled={loading}
                        >
                            <MenuItem value="live">Live</MenuItem>
                            <MenuItem value="pause">Pause</MenuItem>
                        </Select>
                    </FormControl>
                </motion.div>

                <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
                    <motion.div variants={buttonVariants} whileHover="hover">
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            disabled={loading}
                            sx={{ minWidth: 100, color: "#2e7d32", borderColor: "#2e7d32" }}
                        >
                            Cancel
                        </Button>
                    </motion.div>
                    <motion.div variants={buttonVariants} whileHover="hover">
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            sx={{ minWidth: 100, backgroundColor: "#2e7d32" }}
                        >
                            {loading ? <CircularProgress size={24} /> : "Submit"}
                        </Button>
                    </motion.div>
                </Box>
            </Box>

            {/* Snackbar for messages */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </motion.div>
    );
};

export default SliderCreate;